import { useState, useEffect, useRef } from "react";
import {
  Linkedin,
  Copy,
  Check,
  ExternalLink,
  Download,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useCommitMutations } from "../../hooks/useCommits";
import type { Commit, User } from "../../types/api";

const LINKEDIN_CHAR_LIMIT = 3000;

const TYPE_COLORS: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  feat: {
    bg: "rgba(34, 197, 94, 0.1)",
    text: "#22c55e",
    border: "rgba(34, 197, 94, 0.3)",
  },
  fix: {
    bg: "rgba(239, 68, 68, 0.1)",
    text: "#ef4444",
    border: "rgba(239, 68, 68, 0.3)",
  },
  docs: {
    bg: "rgba(59, 130, 246, 0.1)",
    text: "#3b82f6",
    border: "rgba(59, 130, 246, 0.3)",
  },
  chore: {
    bg: "rgba(128, 128, 128, 0.2)",
    text: "#888888",
    border: "rgba(128, 128, 128, 0.3)",
  },
};
const DEFAULT_TYPE_COLOR = {
  bg: "rgba(168, 85, 247, 0.1)",
  text: "#a855f7",
  border: "rgba(168, 85, 247, 0.3)",
};

function getTypeColor(type: string) {
  return TYPE_COLORS[type] || DEFAULT_TYPE_COLOR;
}

function stripMarkdown(text: string): string {
  if (!text) return "";
  return text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/`([^`]+)`/g, '"$1"')
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/\[(.+?)\]\(.+?\)/g, "$1")
    .replace(/!\[.*?\]\(.+?\)/g, "")
    .replace(/^>\s+/gm, "")
    .replace(/^[-*_]{3,}$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function getContentSize(
  message: string,
  body?: string
): "small" | "medium" | "large" {
  const messageLines = Math.ceil((message?.length || 0) / 50);
  const strippedBody = body ? stripMarkdown(body) : "";
  const bodyLines = strippedBody ? strippedBody.split("\n").length : 0;
  const totalLines = messageLines + bodyLines;
  if (totalLines <= 3) return "small";
  if (totalLines >= 8) return "large";
  return "medium";
}

function getSizes(
  size: "small" | "medium" | "large",
  format: "og" | "square" = "og"
) {
  const ogSizes = {
    small: {
      avatar: 48,
      displayName: 22,
      username: 16,
      badgeIcon: 22,
      typeBadge: 22,
      typeBadgePadding: "4px 14px",
      message: 32,
      body: 24,
      bodyGap: 16,
      tagPadding: "8px 16px",
      tagFont: 18,
      headerGap: 14,
      headerMargin: 28,
      usernameMargin: 2,
    },
    medium: {
      avatar: 42,
      displayName: 19,
      username: 15,
      badgeIcon: 19,
      typeBadge: 19,
      typeBadgePadding: "3px 12px",
      message: 24,
      body: 23,
      bodyGap: 14,
      tagPadding: "6px 14px",
      tagFont: 16,
      headerGap: 12,
      headerMargin: 26,
      usernameMargin: 1,
    },
    large: {
      avatar: 36,
      displayName: 17,
      username: 13,
      badgeIcon: 17,
      typeBadge: 17,
      typeBadgePadding: "3px 10px",
      message: 21,
      body: 19,
      bodyGap: 12,
      tagPadding: "5px 12px",
      tagFont: 15,
      headerGap: 10,
      headerMargin: 20,
      usernameMargin: 1,
    },
  };
  const squareSizes = {
    small: {
      avatar: 56,
      displayName: 26,
      username: 19,
      badgeIcon: 26,
      typeBadge: 26,
      typeBadgePadding: "5px 16px",
      message: 38,
      body: 28,
      bodyGap: 18,
      tagPadding: "10px 18px",
      tagFont: 21,
      headerGap: 16,
      headerMargin: 32,
      usernameMargin: 2,
    },
    medium: {
      avatar: 50,
      displayName: 23,
      username: 17,
      badgeIcon: 23,
      typeBadge: 23,
      typeBadgePadding: "4px 14px",
      message: 29,
      body: 27,
      bodyGap: 16,
      tagPadding: "8px 16px",
      tagFont: 19,
      headerGap: 14,
      headerMargin: 30,
      usernameMargin: 2,
    },
    large: {
      avatar: 44,
      displayName: 20,
      username: 15,
      badgeIcon: 20,
      typeBadge: 20,
      typeBadgePadding: "4px 12px",
      message: 25,
      body: 22,
      bodyGap: 14,
      tagPadding: "6px 14px",
      tagFont: 17,
      headerGap: 12,
      headerMargin: 24,
      usernameMargin: 1,
    },
  };
  return format === "square" ? squareSizes[size] : ogSizes[size];
}

type ImageFormat = "og" | "square";

interface CommitImagePreviewProps {
  commit: Commit;
  user: User;
  format: ImageFormat;
  avatarDataUrl?: string | null;
}

function CommitImagePreview({
  commit,
  user,
  format,
  avatarDataUrl,
}: CommitImagePreviewProps) {
  const contentSize = getContentSize(commit.message, commit.body);
  const sizes = getSizes(contentSize, format);
  const typeColor = commit.type ? getTypeColor(commit.type) : null;
  const maxBodyLines = format === "square" ? 20 : 10;
  const bodyLines = commit.body
    ? stripMarkdown(commit.body).split("\n").slice(0, maxBodyLines)
    : [];

  const isSquare = format === "square";
  const width = isSquare ? 1080 : 1200;
  const height = isSquare ? 1080 : 630;
  const cardWidth = isSquare ? 920 : 1040;
  const bgImage = isSquare ? "/square-background.png" : "/og-background.png";
  const logoHeight = isSquare ? 32 : 28;
  const logoMargin = isSquare ? 24 : 20;

  return (
    <div
      style={{
        width,
        height,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: cardWidth,
          backgroundColor: "rgba(30,30,30,0.95)",
          borderRadius: 16,
          padding: 32,
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: sizes.headerGap,
            marginBottom: sizes.headerMargin,
          }}
        >
          {avatarDataUrl || user.avatarUrl ? (
            <img
              src={avatarDataUrl || user.avatarUrl}
              style={{
                width: sizes.avatar,
                height: sizes.avatar,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              alt="Avatar"
            />
          ) : (
            <div
              style={{
                width: sizes.avatar,
                height: sizes.avatar,
                borderRadius: "50%",
                backgroundColor: "#444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: sizes.avatar * 0.5,
                fontWeight: 600,
              }}
            >
              {(user.displayName || user.username)[0].toUpperCase()}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontSize: sizes.displayName,
                  fontWeight: 600,
                  color: "white",
                }}
              >
                {user.displayName || user.username}
              </span>
              {user.badges?.some((b) => b.type === "verified") && (
                <img
                  src="/badges/verified.svg"
                  style={{ width: sizes.badgeIcon, height: sizes.badgeIcon }}
                  alt="Verified"
                />
              )}
            </div>
            <span
              style={{
                fontSize: sizes.username,
                color: "#888",
                fontFamily: "JetBrains Mono, monospace",
                marginTop: sizes.usernameMargin,
              }}
            >
              @{user.username}
            </span>
          </div>
        </div>

        {/* Message */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: sizes.message,
            fontWeight: 500,
            color: "white",
            lineHeight: 1.6,
          }}
        >
          {typeColor && commit.type && (
            <span
              style={{
                padding: sizes.typeBadgePadding,
                backgroundColor: typeColor.bg,
                color: typeColor.text,
                borderRadius: 5,
                border: `1px solid ${typeColor.border}`,
                fontSize: sizes.typeBadge,
                fontWeight: 500,
                marginRight: 8,
              }}
            >
              {commit.type}
            </span>
          )}
          <span style={{ marginRight: 8 }}>:</span>
          {stripMarkdown(commit.message).split(" ").map((word, i, arr) => (
            <span key={i} style={{ marginRight: i < arr.length - 1 ? 8 : 0 }}>
              {word}
            </span>
          ))}
        </div>

        {/* Body */}
        {bodyLines.length > 0 && (
          <div
            style={{ display: "flex", flexDirection: "column", marginTop: 20 }}
          >
            {bodyLines.map((line, i) => (
              <span
                key={i}
                style={{
                  fontSize: line ? sizes.body : sizes.body * 0.5,
                  color: "#aaa",
                  lineHeight: 1.5,
                }}
              >
                {line || "\u00A0"}
              </span>
            ))}
          </div>
        )}

        {/* Tags */}
        {commit.tags.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 20,
            }}
          >
            {commit.tags.slice(0, 5).map((tag) => (
              <div
                key={tag}
                style={{
                  padding: sizes.tagPadding,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  fontSize: sizes.tagFont,
                  color: "#888",
                  fontFamily: "JetBrains Mono, monospace",
                }}
              >
                #{tag}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Logo */}
      <img
        src="/logo_dark.svg"
        style={{ height: logoHeight, marginTop: logoMargin }}
        alt="Logo"
      />
    </div>
  );
}

interface LinkedInShareModalProps {
  commit: Commit | null;
  open: boolean;
  onClose: () => void;
}

export default function LinkedInShareModal({
  commit,
  open,
  onClose,
}: LinkedInShareModalProps) {
  const [editedText, setEditedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imageCopied, setImageCopied] = useState(false);
  const [imageFormat, setImageFormat] = useState<ImageFormat>("square");
  const [copyingImage, setCopyingImage] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [containerWidth, setContainerWidth] = useState(400);
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { generateLinkedInPost } = useCommitMutations();

  const charCount = editedText.length;
  const isOverLimit = charCount > LINKEDIN_CHAR_LIMIT;

  // Measure the wrapper to calculate scale
  useEffect(() => {
    if (!showImage || !wrapperRef.current) return;

    const measure = () => {
      if (wrapperRef.current) {
        setContainerWidth(wrapperRef.current.clientWidth);
      }
    };

    // Small delay to ensure render is complete
    const timer = setTimeout(measure, 10);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
    };
  }, [showImage, imageFormat]);

  useEffect(() => {
    if (open && commit) {
      setLoading(true);
      setCopied(false);
      setImageCopied(false);
      setEditedText("");
      setAvatarDataUrl(null);

      generateLinkedInPost(commit.id)
        .then((result) => {
          setEditedText(result.body);
        })
        .catch((err) => {
          console.error("Failed to generate LinkedIn post:", err);
          toast.error("Failed to generate LinkedIn post");
        })
        .finally(() => setLoading(false));

      // Load avatar as base64 to avoid CORS issues in image generation
      const avatarUrl = commit.user?.avatarUrl;
      if (avatarUrl) {
        fetch(avatarUrl)
          .then((res) => res.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              setAvatarDataUrl(reader.result as string);
            };
            reader.readAsDataURL(blob);
          })
          .catch(() => {
            console.warn("Could not load avatar for image generation");
          });
      }
    }
  }, [open, commit, generateLinkedInPost]);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(editedText);
      setCopied(true);
      toast.success("Text copied to clipboard");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleCopyImage = async () => {
    if (!imageRef.current) return;

    setCopyingImage(true);
    try {
      const dataUrl = await toPng(imageRef.current, {
        quality: 1,
        pixelRatio: 1,
        cacheBust: true,
        skipFonts: true, // Avoid CORS issues with Google Fonts
      });

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Safari requires the blob to be wrapped in a Promise
      const clipboardItem = new ClipboardItem({
        "image/png": Promise.resolve(blob),
      });

      await navigator.clipboard.write([clipboardItem]);

      setImageCopied(true);
      toast.success("Image copied to clipboard");
      setTimeout(() => setImageCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy image:", err);
      toast.error("Copy failed. Please download the image instead.");
    } finally {
      setCopyingImage(false);
    }
  };

  const handleDownloadImage = async () => {
    if (!imageRef.current) return;

    setCopyingImage(true);
    try {
      const dataUrl = await toPng(imageRef.current, {
        quality: 1,
        pixelRatio: 1,
        cacheBust: true,
        skipFonts: true, // Avoid CORS issues with Google Fonts
      });

      const link = document.createElement("a");
      link.download = `commit-${commit?.id}-${imageFormat}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Image downloaded");
    } catch (err) {
      console.error("Failed to download image:", err);
      toast.error("Failed to download image");
    } finally {
      setCopyingImage(false);
    }
  };

  const handleOpenLinkedIn = async () => {
    // Try Web Share API first (works on mobile, pre-fills text)
    if (navigator.share) {
      try {
        await navigator.share({
          text: editedText,
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        if ((err as Error).name === "AbortError") return;
      }
    }

    // Fallback: copy text to clipboard and open LinkedIn
    try {
      await navigator.clipboard.writeText(editedText);
      toast.success("Text copied! Paste it in your LinkedIn post.");
    } catch {
      // Continue even if copy fails
    }
    window.open("https://www.linkedin.com/feed/?shareActive=true", "_blank");
  };

  const user = commit?.user;

  const actualWidth = imageFormat === "square" ? 1080 : 1200;
  const actualHeight = imageFormat === "square" ? 1080 : 630;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="w-[calc(100vw-32px)] max-w-[560px] max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 font-mono text-base sm:text-lg">
            <Linkedin className="h-5 w-5 text-[#0A66C2]" />
            Share to LinkedIn
          </DialogTitle>
          <DialogDescription className="text-sm">
            Copy an image or text to share on LinkedIn.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Spinner size="lg" />
            <p className="text-[hsl(var(--muted-foreground))] mt-4 text-sm">
              Generating post...
            </p>
          </div>
        ) : commit && user ? (
          <div className="space-y-4">
            {/* Text content */}
            <div className="space-y-2">
              <Textarea
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                className="min-h-[140px] font-mono text-sm resize-y"
                placeholder="Write your LinkedIn post..."
              />
              <div className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyText}
                  disabled={isOverLimit}
                  className="text-xs h-7 px-2"
                >
                  {copied ? (
                    <Check className="mr-1 h-3 w-3" />
                  ) : (
                    <Copy className="mr-1 h-3 w-3" />
                  )}
                  {copied ? "Copied!" : "Copy text"}
                </Button>
                <span
                  className={`text-xs font-mono ${
                    isOverLimit
                      ? "text-[hsl(var(--destructive))]"
                      : charCount > LINKEDIN_CHAR_LIMIT * 0.9
                      ? "text-amber-500"
                      : "text-[hsl(var(--muted-foreground))]"
                  }`}
                >
                  {charCount.toLocaleString()} /{" "}
                  {LINKEDIN_CHAR_LIMIT.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Image section - collapsible */}
            <div className="border border-[hsl(var(--border))] rounded-lg overflow-hidden">
              <button
                onClick={() => setShowImage(!showImage)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-[hsl(var(--muted))] transition-colors text-sm"
              >
                <span className="font-medium">Post image</span>
                {showImage ? (
                  <ChevronUp className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                )}
              </button>

              {showImage && (
                <div className="border-t border-[hsl(var(--border))] p-3 space-y-3">
                  {/* Format selector */}
                  <div className="flex gap-2">
                    <Button
                      variant={imageFormat === "square" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setImageFormat("square")}
                      className="flex-1 text-xs"
                    >
                      Square
                    </Button>
                    <Button
                      variant={imageFormat === "og" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setImageFormat("og")}
                      className="flex-1 text-xs"
                    >
                      Wide
                    </Button>
                  </div>

                  {/* Image preview */}
                  <div ref={wrapperRef} className="w-full">
                    {(() => {
                      const scale = containerWidth / actualWidth;
                      return (
                        <div
                          className="rounded-md overflow-hidden bg-black"
                          style={{
                            width: containerWidth,
                            height: actualHeight * scale,
                          }}
                        >
                          <div
                            style={{
                              transform: `scale(${scale})`,
                              transformOrigin: "top left",
                              width: actualWidth,
                              height: actualHeight,
                            }}
                          >
                            <div ref={imageRef}>
                              <CommitImagePreview
                                commit={commit}
                                user={user}
                                format={imageFormat}
                                avatarDataUrl={avatarDataUrl}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Image actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleDownloadImage}
                      disabled={copyingImage}
                      size="sm"
                      className="flex-1 text-xs"
                    >
                      <Download className="mr-1 h-3 w-3" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCopyImage}
                      disabled={copyingImage}
                      size="sm"
                      className="flex-1 text-xs"
                    >
                      {copyingImage ? (
                        <Spinner size="sm" className="mr-1" />
                      ) : imageCopied ? (
                        <Check className="mr-1 h-3 w-3" />
                      ) : (
                        <Copy className="mr-1 h-3 w-3" />
                      )}
                      {imageCopied ? "Copied!" : "Copy image"}
                    </Button>
                  </div>

                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Download and attach the image to your LinkedIn post.
                  </p>
                </div>
              )}
            </div>

            {/* Main action */}
            <Button
              onClick={handleOpenLinkedIn}
              className="w-full bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open LinkedIn
            </Button>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-[hsl(var(--muted-foreground))] text-sm">
              Failed to load commit data
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
